import { isDefined } from '@sama/js-core';
import { groupBy, keyBy, keys, mapValues, partition, values } from 'lodash';
import { ObjectState } from '../../../cross-team-shared/annotation/shared/state/object-state';
import { defaultAnnotationColor } from '../../../cross-team-shared/constants';
import { strictValues } from '../../../cross-team-shared/misc-utils/strict-record';
import { pickExisting } from '../../../shared/misc-utilities/misc-utils';
import {
  AnnotationEntityExternal,
  ObjectExternal,
  ObjectLocationExternal,
  ShapeEntityExternal,
  ShapeLocationEntityExternal,
  SubmissionMetadata,
} from '../../../team-apis/otterdam/annotations';
import { isAnnotationOutput } from '../../h3ml/dsl/sensor-fusion/sensor-fusion-output.utils';
import { getShapesFromShapeInfo } from '../../h3ml/state/task-workspace-state.utils';
import { TaskWorkspaceStore } from '../../h3ml/state/task-workspace-store';
import { TimerService } from '../../production-workspace/timer-badge/timer.service';
import { convertShapesToInternal } from '../../shared/internal-external-converters/shape-external-to-internal-converter';
import {
  appendDynamicTag,
  convertObjectExternalToObjectInternal,
  isObjectEntity,
  isObjectLocationEntity,
  isObjectRelatedEntity,
  isShapeRelatedEntity,
  isSubmissionMetadata,
} from '../../shared/utils/annotation-external-utils';
import { getDefaultObjectTransientInfo } from '../../shared/utils/object-utils';

export function initializeAnnotations(
  taskWorkspaceStore: TaskWorkspaceStore,
  timerService: TimerService,
  annotations: AnnotationEntityExternal[],
) {
  initializeShapes(
    taskWorkspaceStore,
    annotations.filter(isShapeRelatedEntity),
  );
  initializeObjects(
    taskWorkspaceStore,
    annotations.filter(isObjectRelatedEntity),
  );
  initializeSubmissionMetadata(
    taskWorkspaceStore,
    timerService,
    annotations.find(isSubmissionMetadata),
  );
}

export function initializeShapes(
  taskWorkspaceStore: TaskWorkspaceStore,
  annotations: Array<ShapeEntityExternal | ShapeLocationEntityExternal>,
) {
  const project = taskWorkspaceStore.state().project;
  const annotationOutputs = project.outputs.filter(isAnnotationOutput);

  annotationOutputs.forEach((output) => {
    // Shape groups and order do not exist in new projects with objects.
    // Adding empty data structure to satisfy the type check.
    taskWorkspaceStore('outputAnnotationInfo')(output.name).assign({
      shapes: convertShapesToInternal(annotations, output),
      groups: {},
      shapeAndGroupOrder: [],
    });
  });

  createShapeTransientInfo(taskWorkspaceStore);
}

export function initializeTrainingShapes(
  taskWorkspaceStore: TaskWorkspaceStore,
  annotations: Array<ShapeEntityExternal | ShapeLocationEntityExternal>,
) {
  const project = taskWorkspaceStore.state().project;
  const annotationOutputs = project.outputs.filter(isAnnotationOutput);

  taskWorkspaceStore.batch(() => {
    annotationOutputs.forEach((output) => {
      taskWorkspaceStore('outputAnnotationInfo')(output.name).assign({
        trainingShapes: convertShapesToInternal(annotations, output),
      });
    });
  });
}

export function buildObjectsWithoutLocation(
  annotations: Array<ObjectLocationExternal | ObjectExternal>,
) {
  const objects = keyBy(
    annotations
      .filter(isObjectEntity)
      .map(convertObjectExternalToObjectInternal),
    'index',
  );
  populateChildObjectIds(strictValues(objects));
  return objects;
}

export function initializeObjects(
  taskWorkspaceStore: TaskWorkspaceStore,
  annotations: Array<ObjectLocationExternal | ObjectExternal>,
) {
  let objects: StrictRecord<number, ObjectState>;

  if (taskWorkspaceStore.isUsingGenAi()) {
    objects = buildObjectsWithoutLocation(annotations);
  } else {
    objects = buildObjectsWithLocations(annotations);
    populateChildAnnotationArrays(taskWorkspaceStore, strictValues(objects));
  }

  taskWorkspaceStore.assign({
    objects,
    objectTransientInfo: mapValues(objects, () =>
      getDefaultObjectTransientInfo(),
    ),
    objectSelection: {},
    scopedObjectId: undefined,
  });
}

export function initializeSubmissionMetadata(
  taskWorkspaceStore: TaskWorkspaceStore,
  timerService: TimerService,
  metadata: SubmissionMetadata | undefined,
) {
  const nextIds = pickExisting(
    metadata,
    'nextShapeId',
    'nextObjectId',
    'nextObjectClass',
  );

  if (nextIds.nextObjectId === undefined) {
    const objectIds = keys(taskWorkspaceStore.state().objects).map(Number);
    nextIds.nextObjectId = Math.max(-1, ...objectIds) + 1;
  }

  if (nextIds.nextShapeId === undefined) {
    const shapeIds = getShapesFromShapeInfo(
      taskWorkspaceStore.state().outputAnnotationInfo,
    ).map((shape) => shape.index);
    nextIds.nextShapeId = Math.max(0, ...shapeIds) + 1;
  }
  if (taskWorkspaceStore.state().workspace === 'production') {
    const step = taskWorkspaceStore.getStep();
    timerService.start(step.target_duration, {
      savedDuration: metadata?.duration,
      isVisible: step.target_duration_enabled,
    });
  }

  taskWorkspaceStore.assign(nextIds);
}

export function resetPreviewAnnotations(
  taskWorkspaceStore: TaskWorkspaceStore,
) {
  taskWorkspaceStore.batch(() => {
    taskWorkspaceStore('objects').set({});
    taskWorkspaceStore('objectTransientInfo').set({});
    taskWorkspaceStore('shapeTransientInfo').set({});
    taskWorkspaceStore('scopedObjectId').set(undefined);
    taskWorkspaceStore('objectSelection').set({});
  });
}

function buildObjectsWithLocations(
  annotations: Array<ObjectLocationExternal | ObjectExternal>,
) {
  const [objectLocationExternals, objectExternals] = partition(
    annotations,
    isObjectLocationEntity,
  );

  const objects: StrictRecord<number, ObjectState> = keyBy(
    objectExternals.map(convertObjectExternalToObjectInternal),
    'index',
  );

  objectLocationExternals.forEach((objectLocationExternal) => {
    objects[objectLocationExternal.objectId]!.dynamic_tags = appendDynamicTag(
      objectLocationExternal,
      objects[objectLocationExternal.objectId]!.dynamic_tags,
    );
  });

  return objects;
}

function populateChildAnnotationArrays(
  taskWorkspaceStore: TaskWorkspaceStore,
  objects: ObjectState[],
) {
  populateChildObjectIds(objects);
  const allShapes = getShapesFromShapeInfo(
    taskWorkspaceStore.state().outputAnnotationInfo,
  );
  const childShapes = groupBy(allShapes, (shape) => shape.parentId);

  objects.forEach((object) => {
    const objectId = object.index.toString();
    object.childShapeIds =
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
      childShapes[objectId]?.map((childShape) => childShape.index) ?? [];
  });
}

function populateChildObjectIds(objects: ObjectState[]) {
  const childObjects: StrictRecord<string, ObjectState[]> = groupBy(
    objects,
    (object) => object.parentId,
  );

  objects.forEach((object) => {
    const objectId = object.index.toString();
    object.childObjectIds =
      childObjects[objectId]?.map((childObject) => childObject.index) || [];
  });
}

function createShapeTransientInfo(taskWorkspaceStore: TaskWorkspaceStore) {
  const shapes = keyBy(
    values(taskWorkspaceStore.state().outputAnnotationInfo)
      .filter(isDefined)
      .map((shapeInfo) => strictValues(shapeInfo.shapes))
      .flat(),
    (shapeState) => shapeState.index,
  );
  const shapeTransientInfo = mapValues(shapes, (shape, _) => ({
    index: +shape.index,
    isFilteredOut: false,
    color: defaultAnnotationColor,
    hasMissingRequiredTags: false,
  }));

  taskWorkspaceStore('shapeTransientInfo').set(shapeTransientInfo);
}
